import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { UserService } from '../../service/data/user.service';
import { User } from '../../model/user';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { DataService } from '../../../core/service/base/data.service';
import { FormGroup, FormBuilder } from '@angular/forms';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { UserAccountService } from '../../service/user-account.service';
import { ActivatedRoute } from '@angular/router';
import { standardCommand } from '../../../core/model/command';
import { SelectionItem } from '../../../core/component/select/select.component';
import { UserGroupService } from '../../service/data/user-group.service';
import { IdNumberValidatorService } from '../../service/id-number-validator-service';
import { ExecutorGroupService } from '../../service/data/executor-group.service';
import { forkJoin, Subscription } from 'rxjs';
import { UserGroup } from '../../model/user-group';
import { LanguageService } from '../../../core/service/language.service';

class AppliedAccess {
  constructor(feature: string, appliedBy: string = '', appliedByName: string = '') {
    this.feature = feature;
    this.featureName = 'FEATURE.NAMES.' + feature.toUpperCase();

    this.appliedBy = appliedBy ? [appliedBy] : [];
    this.appliedByNames = appliedByName ? [appliedByName] : [];
  }

  feature: string;
  featureName: string;
  appliedBy: string[];
  appliedByNames: string[];
}

@Component({
    selector: 'app-user-form',
    templateUrl: './user-form.component.html',
    styleUrls: ['./user-form.component.scss'],
    providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: UserService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserFormComponent extends FormComponentBase<User> implements OnDestroy {
  constructor(
    private _account: UserAccountService,
    private _fb: FormBuilder,
    private _fcf: FormControlExFactory,
    private _userGroupService: UserGroupService,
    private _executorGroupDataService: ExecutorGroupService,
    private _idValidatorService: IdNumberValidatorService,
    activatedRoute: ActivatedRoute,
    dataService: UserService,
    dispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService,
    private _languageService: LanguageService
  ) {
    super(activatedRoute, dataService, messageService, navigation, dispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }

  protected createModel(): User {
    return new User();
  }

  userSubscriptions: Subscription = new Subscription();
  userGroupsSelection: SelectionItem[] = [];
  executorGroupsSelection: SelectionItem[] = [];
  userGroups: UserGroup[] = [];
  allAccesses: AppliedAccess[] = [];
  appliedAccesses: AppliedAccess[] = [];
  readonly civicNumberMask = /^(\d{8}\-\d{0,4})|(\d{1,8})/;
  readonly civicNumberMaskFormat: string = 'ÅÅÅÅMMDD-NNNN';
  showUserGroupPermissions: boolean = true;

  protected init(data: User) {
    if (data) {
      forkJoin({
        userGroups: this._userGroupService.getUserGroupsWithFeatures(),
        executorGroups: this._executorGroupDataService.getList(),
      }).subscribe(results => {
        this.userGroupsSelection = results.userGroups.map(group => new SelectionItem(group.name, group.id));
        this.executorGroupsSelection = results.executorGroups.map(group => new SelectionItem(group.name, group.id));
        this.userGroups = results.userGroups;
        super.init(data);
    const helpTextPrefix = this._languageService.instant('USERGROUP.PERMISSIONS.SOURCES');

        this.userGroups.forEach(group => {
          group.features.forEach(feature => {
            const appliedAccess = this.allAccesses.find(x => x.feature == feature);
            const isUserGroupIncluded = this.dataContext.userGroups.includes(group.id);

            if (appliedAccess) {
              if (isUserGroupIncluded) {
                appliedAccess.appliedBy.push(group.id);
                appliedAccess.appliedByNames.push(group.name);
              }
            } else {
              if (isUserGroupIncluded) {
                this.allAccesses.push(new AppliedAccess(feature, group.id, group.name));
              } else {
                this.allAccesses.push(new AppliedAccess(feature));
              }
            }
          });
        });

        this.allAccesses.sort((a, b) => a.feature.localeCompare(b.feature, undefined, { sensitivity: 'base' }));

        this.updateAppliedAccesses();
      });
    }
  }

  protected getFormGroup(): FormGroup {
    const group = this._fb.group({
      name: this._fcf.createMandatory('USER.NAME', this.dataContext.name),
      password: this._fcf.createMandatory('USER.PASSWORD', this.dataContext.password),
      personName: this._fcf.create('USER.NAMEOFUSER', this.dataContext.personName),
      phoneNumber: this._fcf.create('USER.PHONENUMBER', this.dataContext.phoneNumber),
      civicNumber: this._fcf.createWithOptions('USER.CIVICNUMBER', this.dataContext.civicNumber, {
        validator: this._idValidatorService.swedishSSN,
      }),
    });

    if (!this.dataContext.isNew) {
      group.controls['name'].disable();
      if (!this.dataContext.password) {
        group.removeControl('password');
      } else {
        group.controls['password'].disable();
      }
    }
    for (let userGroup of this.userGroupsSelection) {
      const userGroupItem = this._fcf.create(userGroup.name, this.dataContext.userGroups.includes(userGroup.value));

      group.addControl(userGroup.value, userGroupItem);

      this.userSubscriptions.add(
        userGroupItem.valueChanges.subscribe(newValue => {
          const userGroupEntity = this.userGroups.find(x => x.id == userGroup.value);
          userGroupEntity.features.forEach(feature => {
            const appliedAccess = this.allAccesses.find(aa => aa.feature == feature);
            if (newValue) {
              if (!appliedAccess.appliedBy.includes(userGroup.value)) {
                appliedAccess.appliedBy = [...appliedAccess.appliedBy, userGroup.value];
              }

              if (!appliedAccess.appliedByNames.includes(userGroup.name)) {
                appliedAccess.appliedByNames = [...appliedAccess.appliedByNames, userGroup.name].sort();
              }
            } else {
              appliedAccess.appliedBy = appliedAccess.appliedBy.filter(x => x !== userGroup.value);
              appliedAccess.appliedByNames = appliedAccess.appliedByNames.filter(x => x !== userGroup.name).sort();
            }

            this.updateAppliedAccesses();
          });
        })
      );
    }

    for (let executorGroup of this.executorGroupsSelection) {
      group.addControl(
        executorGroup.value,
        this._fcf.create(executorGroup.name, this.dataContext.executorGroups.includes(executorGroup.value))
      );
    }

    return group;
  }

  private updateAppliedAccesses(): void {
    this.appliedAccesses = this.allAccesses.filter(x => x.appliedBy.length > 0);
  }

  protected prepareSave(): User {
    const model = super.prepareSave();

    var formValue = this.form.value;
    model.name = this.form.controls['name'].value;
    model.userGroups = this.userGroupsSelection.filter(group => formValue[group.value]).map(group => group.value);
    model.executorGroups = this.executorGroupsSelection.filter(group => formValue[group.value]).map(group => group.value);

    return model;
  }

  protected override formSubmittedChange(response?: any): void {
    super.formSubmittedChange(response);
    this._account.refreshAccountDefinition();
  }

  getUserGroupFeatures(userGroupId: string): string[] {
    return this.userGroups.find(x => x.id == userGroupId)?.features?.map(x => 'FEATURE.NAMES.' + x.toUpperCase()) ?? [];
  }

  hasUserGroupFeatures(userGroupId: string): boolean {
    return this.getUserGroupFeatures(userGroupId).length > 0;
  }

  toggleShowUserGroupPermissions() {
    this.showUserGroupPermissions = !this.showUserGroupPermissions;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.userSubscriptions?.unsubscribe();
  }

  getHelperText(userGroupNames: string[]): string {
    const prefix = this._languageService.instant('USERGROUP.PERMISSIONS.SOURCES');
    const items = userGroupNames.join('\n');
    return `${prefix}\n${items}`;
  }
}
